import { useEffect, useMemo, useState } from "react";
import { useFilter } from "react-aria";
import { listBrokerages } from "../api";
import { useDebouncedValue } from "./use-debounced-value";

interface BrokerageOption {
    id: string;
    label: string;
}

/**
 * Server-side search-as-you-type for the Brokerage filter selects (Triage
 * and Policies). Typing re-queries `/v2/brokerages/` with the debounced
 * term, so every brokerage is reachable instead of only the first page
 * (OR-1792). Requests use the endpoint's max page size (200), so a broad
 * term still lists every match at the current volume.
 *
 * Fresh results are shown as returned (the backend also matches email
 * domain). While a response for the current text is pending, or after it
 * failed, the previous results are narrowed client-side so options that
 * don't match what was typed are never offered. The selected option stays
 * pinned so the trigger keeps its label even when the current results
 * don't include it.
 */
export function useBrokerageOptions(selected: BrokerageOption | null) {
    const [results, setResults] = useState<{ query: string; options: BrokerageOption[] }>({ query: "", options: [] });
    const [query, setQuery] = useState("");
    const trimmedQuery = query.trim();
    const debouncedQuery = useDebouncedValue(trimmedQuery, 300);
    // Same matcher SearchSelect uses internally — accent/case-insensitive.
    const { contains } = useFilter({ sensitivity: "base" });

    useEffect(() => {
        let cancelled = false;
        void listBrokerages({ search: debouncedQuery || undefined, limit: 200 })
            .then(
                (rows) =>
                    !cancelled && setResults({ query: debouncedQuery, options: rows.map((b) => ({ id: b.id, label: b.name })) }),
            )
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [debouncedQuery]);

    const items = useMemo(() => {
        const stale = results.query !== trimmedQuery;
        const matched = stale && trimmedQuery ? results.options.filter((o) => contains(o.label, trimmedQuery)) : results.options;
        return selected && !matched.some((o) => o.id === selected.id) ? [selected, ...matched] : matched;
    }, [results, trimmedQuery, selected, contains]);

    return { items, onSearchChange: setQuery };
}
